import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ImagePlus, Images, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, fileUrl, formatApiError } from "@/lib/api";

export const GalleryEditor = ({ items, onChange, worldId }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(0);
  const pick = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    setBusy(files.length);
    const added = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("world_id", worldId);
      try { added.push({ image: (await api.post("/upload", fd)).data.path, caption: "" }); } catch (err) { toast.error(`${file.name}: ${formatApiError(err)}`); }
      setBusy((b) => b - 1);
    }
    if (added.length) onChange((cur) => [...cur, ...added]);
  };
  const edit = (i, patch) => onChange((cur) => cur.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  return (
    <div className="space-y-2" data-testid="gallery-editor">
      <input ref={ref} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid="gallery-input" />
      {items.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {items.map((it, i) => (
            <div key={`${it.image}-${i}`} className="rounded-lg border border-line bg-canvas overflow-hidden" data-testid={`gallery-edit-item-${i}`}>
              <div className="relative h-24 bg-black/40">
                <img src={fileUrl(it.image)} alt="" className="w-full h-full object-contain" />
                <button type="button" onClick={() => onChange((cur) => cur.filter((_, j) => j !== i))} title="Remove image" data-testid={`gallery-remove-${i}`}
                  className="absolute top-1 right-1 p-1 rounded-full bg-black/70 text-white hover:bg-rose-600 transition-colors"><X className="w-3 h-3" /></button>
              </div>
              <Input value={it.caption} maxLength={300} onChange={(e) => edit(i, { caption: e.target.value })} placeholder="Caption"
                className="h-7 text-xs rounded-none border-0 border-t border-line bg-transparent" data-testid={`gallery-caption-${i}`} />
            </div>
          ))}
        </div>
      )}
      <button type="button" onClick={() => ref.current?.click()} disabled={busy > 0} data-testid="gallery-add-btn"
        className="w-full h-11 rounded-xl border border-dashed border-line text-xs text-slate-500 hover:text-amber-400 hover:border-amber-500/40 flex items-center justify-center gap-2 transition-colors">
        {busy > 0 ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading {busy}…</> : <><ImagePlus className="w-4 h-4" /> Add images</>}
      </button>
    </div>
  );
};

const Lightbox = ({ items, at, setAt }) => {
  const it = items[at];
  const step = (d) => setAt((i) => (i + d + items.length) % items.length);
  useEffect(() => {
    const key = (e) => { if (e.key === "ArrowRight") step(1); if (e.key === "ArrowLeft") step(-1); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });
  return (
    <Dialog open onOpenChange={() => setAt(null)}>
      <DialogContent className="max-w-5xl bg-canvas border-line p-3 sm:p-5" data-testid="gallery-lightbox">
        <DialogTitle className="sr-only">Image {at + 1} of {items.length}</DialogTitle>
        <DialogDescription className="sr-only">{it.caption || "Gallery image"}</DialogDescription>
        <div className="relative flex items-center justify-center bg-black/40 rounded-lg">
          <img src={fileUrl(it.image)} alt={it.caption} className="max-h-[75vh] w-auto object-contain" data-testid="gallery-lightbox-image" />
          {items.length > 1 && [[-1, ChevronLeft, "left-2", "prev"], [1, ChevronRight, "right-2", "next"]].map(([d, Icon, pos, id]) => (
            <button key={id} onClick={() => step(d)} data-testid={`gallery-${id}-btn`} className={`absolute ${pos} p-2 rounded-full bg-black/60 text-white hover:bg-amber-500 hover:text-onaccent transition-colors`}><Icon className="w-5 h-5" /></button>
          ))}
        </div>
        <div className="flex items-baseline justify-between gap-4 pt-2">
          <p className="font-display text-xl text-amber-50" data-testid="gallery-lightbox-caption">{it.caption}</p>
          <span className="text-xs font-code text-slate-500 shrink-0">{at + 1} / {items.length}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Gallery = ({ items }) => {
  const [at, setAt] = useState(null);
  if (!items?.length) return null;
  return (
    <section className="mt-10" data-testid="page-gallery">
      <h2 className="font-display text-2xl text-amber-100 flex items-center gap-2 mb-4"><Images className="w-5 h-5 text-amber-500" /> Gallery <span className="text-slate-600 text-lg">{items.length}</span></h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((it, i) => (
          <button key={i} onClick={() => setAt(i)} style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }} data-testid={`gallery-item-${i}`}
            className="rise group text-left rounded-xl overflow-hidden border border-line bg-panel hover:border-amber-500/50 transition-colors">
            <div className="aspect-square bg-black/30 overflow-hidden"><img src={fileUrl(it.image)} alt={it.caption} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
            {it.caption && <p className="text-xs text-slate-400 px-2.5 py-2 line-clamp-2">{it.caption}</p>}
          </button>
        ))}
      </div>
      {at !== null && <Lightbox items={items} at={at} setAt={setAt} />}
    </section>
  );
};
